/* ZemanatList.js — ضمانتنامه و تمدید/تقلیل آن */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TabBar } from '../TabManager';
import JalaliDatePicker from '../../components/JalaliDatePicker';
import useColumnResize from '../../hooks/useColumnResize';
import useTablePagination from '../../hooks/useTablePagination';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

/* ── کمکی‌ها ── */
const NO_CLIENT_SEARCH = [];
const toFa   = n => n===null||n===undefined?'':String(n).replace(/[0-9]/g,d=>'۰۱۲۳۴۵۶۷۸۹'[d]);
const numFmt = n => { const v=Number(n||0); return v===0?'':v.toLocaleString('fa-IR'); };
const API    = (path,opts={}) => fetch(`/api/zemanat${path}`,{
  headers:{'Content-Type':'application/json',Authorization:`Bearer ${localStorage.getItem('access')}`},...opts,
}).then(jsonOf, netErr);

/* زیرکامپوننت‌ها بیرون — وگرنه فوکوس می‌پره */
const FInput = ({label,name,value,onChange,readOnly,flex=1,type='text',dir}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <input name={name} type={type} value={value??''} onChange={onChange} readOnly={readOnly}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',
        direction:dir||'rtl',textAlign:dir==='ltr'?'left':'right',
        width:'100%',boxSizing:'border-box',background:readOnly?'#f5f5f5':'#fff'}}/>
  </div>
);

const FSelect = ({label,name,value,onChange,options,flex=1,disabled=false}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <select name={name} value={value??''} onChange={onChange} disabled={disabled}
      style={{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',
        fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',
        width:'100%',boxSizing:'border-box',background:disabled?'#f5f5f5':'#fff'}}>
      {options.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const FDate = ({label,value,onChange,flex=1,disabled=false}) => (
  <div style={{display:'flex',flexDirection:'column',gap:'4px',flex}}>
    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>{label}</label>
    <JalaliDatePicker value={value} onChange={onChange} placeholder={label} disabled={disabled}/>
  </div>
);

const FCheck = ({label,checked,onChange,flex,disabled=false}) => (
  <label style={{display:'flex',alignItems:'center',gap:'6px',fontSize:'12px',fontWeight:'600',
    color:'#444',cursor:'pointer',flex,paddingTop:'20px'}}>
    <input type="checkbox" checked={!!checked} onChange={onChange} disabled={disabled}/><span>{label}</span>
  </label>
);

/* انواعی که تاریخ صدور/سررسید نمی‌خواهند:
   ۲ سفته، ۴ وجه نقد واریزی، ۶ گواهی مطالبات قطعی قراردادها، ۸ وثیقه ملکی */
const KINDS_WITHOUT_DATES = [2,4,6,8];
const KIND_PROPERTY = 8;        // وثیقه ملکی
/* فیلدهای ثبت جدید و ویرایش — ابطال و ضبط فقط با دکمه‌های خودشان */
const MAIN_EDIT_KEYS = ['kind','subject','prepayment_type','monaghese','zemanat_no','sepam','contract_id',
  'contractor_code','zemanat_subject_id','amount','property_value','branch_code','issu_date','end_date',
  'request_validity_no','request_validity_date','validity_no','validity_date','request_no','request_date',
  'save_date','case_no','remark'];
const BLACK_LIST_WARNING = 'اخطار: این پیمانکار در Black List  قرار گرفته است';
/* فیلدهای تب اصلی — برای انتخاب تب اول در ویرایش قفل‌دار */
const MAIN_TAB_KEYS = ['kind','subject','prepayment_type','zemanat_no','sepam','amount','property_value',
  'contract_id','zemanat_subject_id','branch_code','issu_date','end_date','monaghese','case_no','remark'];
const SUBJECT_PREPAYMENT = 1;   // پیش پرداخت

const STATES = [
  {val:'all',      label:'همه'},
  {val:'active',   label:'معتبر'},
  {val:'cancelled',label:'باطل شده'},
  {val:'given',    label:'ضبط شده'},
];

/* ستون‌های گرید اصلی */
const INIT_WIDTHS = [45,60,120,110,140,130,110,220,180,140,150,110,110,100,100,110,90,180];

const EMPTY = {kind:1,subject:0,prepayment_type:0,monaghese:0,zemanat_no:'',sepam:'',
  contract_id:0,contract_no:'',contractor:'',contractor_code:0,zemanat_subject_id:0,
  amount:'',property_value:'',branch_code:0,branch:'',issu_date:'',end_date:'',
  request_validity_no:'',request_validity_date:'',validity_no:'',validity_date:'',
  cancel_no:'',cancel_date:'',cancel_bank_no:'',cancel_bank_date:'',
  give_no:'',give_date:'',give_bank_no:'',give_bank_date:'',
  give_branch_code:0,give_account_no:'',request_no:'',request_date:'',
  give_request_no:'',give_request_date:'',save_date:'',case_no:'',remark:''};

const EMPTY_TRANS = {trans_id:0,new_amount:'',new_end_date:'',
  receive_no:'',receive_date:'',send_no:'',send_date:'',remark:'',
  recipient1:0,send_no1:'',send_date1:'',recipient2:0,send_no2:'',send_date2:'',
  recipient3:0,send_no3:'',send_date3:'',recipient4:0,send_no4:'',send_date4:''};
const RECIPIENTS = ['سرپرستی بانک','شعبه بانک'];
/* مبلغ با جداکنندهٔ سه‌رقمی نمایش داده و بدون آن فرستاده می‌شود */
const amountText = v => { const d=String(v??'').replace(/[۰-۹]/g,c=>'۰۱۲۳۴۵۶۷۸۹'.indexOf(c)).replace(/[^0-9]/g,'');
  return d?Number(d).toLocaleString('en-US'):''; };
const amountValue = v => String(v??'').replace(/[^0-9]/g,'');

export default function ZemanatList({ user, onBack }) {
  // دکمه‌های فرم FRMZEMANAT — دیدن = دسترسی
  const vbf = useFormButtons('FRMZEMANAT', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK","BTNSETCANCLE","BTNSETGIVE"]);
  // دکمه‌های فرم FRMZEMANATTRANSACTION — دیدن = دسترسی
  const vbf_zemanattransaction = useFormButtons('FRMZEMANATTRANSACTION', ["BTNNEW","BTNEDIT","BTNDELETE","BTNOK","BTNCANCEL"]);
  /* ── گرید اصلی ── */
  const [rows,setRows]         = useState([]);
  const [total,setTotal]       = useState(0);
  const [sumAmount,setSumAmount] = useState(0);
  const [hasMore,setHasMore]   = useState(false);
  const [loading,setLoading]   = useState(false);
  const [selected,setSelected] = useState(null);

  /* ── فیلترها ── */
  const [kind,setKind]     = useState('');
  const [subject,setSubject] = useState('');
  const [contractor,setContractor] = useState(0);
  const [state,setState]   = useState('all');
  const [ready,setReady]   = useState(false);

  /* ── فهرست‌های کد ── */
  const [meta,setMeta] = useState({kinds:[],subjects:[],prepayments:[],
    recipients:[],trans_repair:{fixed:0,with_document:[]},contractors:[],stats:{}});
  const [subjectList,setSubjectList] = useState([]);

  /* ── مودال‌ها ── */
  const [modal,setModal]   = useState(null);   // new | edit | delete | trans
  const [tab,setTab]       = useState('main'); // main | validity | cancel | give
  const [form,setForm]     = useState(EMPTY);
  const [error,setError]   = useState('');
  /* قفل ویرایش بعد از صدور سند — null یعنی همهٔ فیلدها باز است */
  const [editLock,setEditLock] = useState(null);   // {allowed:[...], messages:[...], infos:[...], mode}
  /* علت ابطال (برای ضمانتنامهٔ دارای قرارداد و غیر پیش‌پرداخت) */
  const [reasonBox,setReasonBox] = useState(null);  // {reasons, need_remark, title, letter_no, letter_date, remark, lock}
  const [cancelReason,setCancelReason] = useState(null);
  /* اطلاعات کمکی ردیف انتخاب‌شده */
  const [rowInfo,setRowInfo] = useState({black_list:'',cancel_blocked:false,prepayment_message:''});
  const [hidePrepayMsg,setHidePrepayMsg] = useState(false);
  const [prepayMsgFor,setPrepayMsgFor] = useState(0);
  const [giveBranchLookup,setGiveBranchLookup] = useState([]);
  const [toast,setToast]   = useState('');
  const [lookup,setLookup] = useState([]);
  const [branchLookup,setBranchLookup] = useState([]);

  /* ── تمدید و تقلیل ── */
  const [trans,setTrans]         = useState([]);
  const [transForm,setTransForm] = useState(EMPTY_TRANS);
  const [transError,setTransError] = useState('');
  const [transMode,setTransMode]   = useState('view');   // view | new | edit
  const [transSel,setTransSel]     = useState(null);

  const offsetRef = useRef(0);
  const [searchQ,setSearchQ] = useState('');
  /* متن تایپ‌شده در کادر قرارداد — جدا از قرارداد انتخاب‌شده */
  const [contractQ,setContractQ] = useState('');
  const contractTimer = useRef(null);

  const {TH} = useColumnResize(INIT_WIDTHS);
  const {visibleRows,search,setSearch,totalCount,LoadMoreButton} = useTablePagination({
    // جستجو سمت سرور انجام می‌شود (روی همهٔ ضمانتنامه‌ها، نه فقط ۲۰۰ ردیف لود شده)
    allRows:rows, searchFields:NO_CLIENT_SEARCH,
    serverTotal:total, serverHasMore:hasMore,
  });

  const showToast = msg => { setToast(msg); setTimeout(()=>setToast(''),2800); };

  /* مقداردهی اولیه */
  useEffect(()=>{ (async()=>{
    try{
      const d = await API('/zemanats/init/');
      if(d.ok) setMeta(d);
      const sub = await API('/subjects/lookup/');
      if(sub.ok) setSubjectList(sub.rows||[]);
    }catch(e){console.error(e);}
    setReady(true);
  })(); },[]);

  const fetchData = useCallback(async(reset=false)=>{
    if(reset){ offsetRef.current=0; setRows([]); setSelected(null); }
    setLoading(true);
    try{
      const off = reset?0:offsetRef.current;
      const d = await API(`/zemanats/?offset=${off}&kind=${kind}&subject=${subject}`
        +`&contractor_code=${contractor}&state=${state}`
        +`&search=${encodeURIComponent(searchQ)}`);
      if(d.ok){
        setRows(p=>reset?d.rows:[...p,...d.rows]);
        setTotal(d.total); setSumAmount(d.sum_amount); setHasMore(d.has_more);
        offsetRef.current = (reset?0:offsetRef.current)+d.rows.length;
      }
    }catch(e){console.error(e);}
    setLoading(false);
  },[kind,subject,contractor,state,searchQ]);

  useEffect(()=>{ if(ready) fetchData(true); },[ready,kind,subject,contractor,state,searchQ]); // eslint-disable-line

  /* جستجو با کمی مکث بعد از تایپ فرستاده می‌شود تا با هر حرف درخواست نرود */
  useEffect(()=>{
    const h = setTimeout(()=>setSearchQ(search.trim()), 400);
    return ()=>clearTimeout(h);
  },[search]);

  const handleChange = e => { const{name,value}=e.target; setForm(p=>({...p,[name]:value})); };

  /* ── جدید / ویرایش ── */
  const openNew = ()=>{ if (!vbf.can('BTNNEW')) return;
    setEditLock({allowed:MAIN_EDIT_KEYS, messages:[], infos:[], mode:'new'}); setCancelReason(null);
    setForm(EMPTY); setContractQ(''); setError(''); setLookup([]); setTab('main'); setModal('new'); };

  /* فرم ویرایش / ابطال / ضبط با فیلدهای مجاز همان عملیات باز می‌شود */
  const openWithLock = (op, d, firstTab) => {
    setEditLock({allowed:d.allowed||[], messages:d.messages||[], infos:d.infos||[], mode:d.mode,
      black:d.black_list?BLACK_LIST_WARNING:''});
    setForm({...EMPTY, ...selected, branch:selected.branch_title||'', give_branch:selected.give_branch_title||''});
    setContractQ(''); setError(''); setLookup([]); setBranchLookup([]); setGiveBranchLookup([]);
    const allowed = d.allowed||[];
    const first = firstTab
      || (allowed.some(k=>MAIN_TAB_KEYS.includes(k)) ? 'main'
        : allowed.some(k=>k.startsWith('cancel_')) ? 'cancel'
        : allowed.some(k=>k.startsWith('give_')) ? 'give' : 'main');
    setTab(first); setModal(op);
  };
  const openEdit = async()=>{ if (!vbf.can('BTNEDIT')) return;
    if(!selected) return;
    const d = await API(`/zemanats/${selected.id}/edit-mode/`);
    if(!d.ok){ alert(d.error||'خطا'); return; }
    setCancelReason(null);
    openWithLock('edit', d);
  };
  const openCancel = async()=>{ if (!vbf.can('BTNSETCANCLE')) return;
    if(!selected) return;
    const d = await API(`/zemanats/${selected.id}/cancel/check/`);
    if(!d.ok){ alert([...(d.infos||[]), d.error||'خطا'].join('\n\n')); return; }
    if((d.infos||[]).length) alert(d.infos.join('\n\n'));
    setCancelReason(null);
    if(d.need_reason){
      setReasonBox({reasons:d.reasons, need_remark:d.reasons_need_remark||[], title:'',
        letter_no:'', letter_date:'', remark:'', lock:d, error:''});
      return;
    }
    openWithLock('cancel', d, (d.allowed||[]).includes('cancel_no')?'cancel':undefined);
  };
  /* «ادامه» در کادر علت ابطال */
  const confirmReason = ()=>{
    const r = reasonBox;
    if(!String(r.letter_no||'').trim()) { setReasonBox(p=>({...p,error:'شماره نامه را وارد کنید'})); return; }
    if(String(r.letter_date||'').length!==10) { setReasonBox(p=>({...p,error:'تاریخ نامه معتبر نیست'})); return; }
    if(r.need_remark.includes(r.title) && String(r.remark||'').trim().length<10){
      setReasonBox(p=>({...p,error:'توضیح ابطال معتبر نیست'})); return; }
    setCancelReason({title:r.title, letter_no:r.letter_no, letter_date:r.letter_date, remark:r.remark});
    setReasonBox(null);
    openWithLock('cancel', r.lock, 'cancel');
  };
  const openGive = async()=>{ if (!vbf.can('BTNSETGIVE')) return;
    if(!selected) return;
    const d = await API(`/zemanats/${selected.id}/give/check/`);
    if(!d.ok){ alert(d.error||'خطا'); return; }
    setCancelReason(null);
    openWithLock('give', d, (d.allowed||[]).includes('give_no')?'give':undefined);
  };
  /* آیا این فیلد در حالت فعلی فرم قفل است؟ */
  const ro = key => ['new','edit','cancel','give'].includes(modal) && !!editLock && !editLock.allowed.includes(key);

  /* اطلاعات کمکی ردیف: لیست سیاه، امکان ابطال، پیام پیش‌پرداخت */
  useEffect(()=>{
    if(!selected){ setRowInfo({black_list:'',cancel_blocked:false,prepayment_message:''}); return; }
    let alive = true;
    API(`/zemanats/${selected.id}/row-info/`).then(d=>{
      if(alive && d.ok){ setRowInfo(d); setPrepayMsgFor(selected.id); }
    });
    return ()=>{ alive=false; };
  },[selected?.id]); // eslint-disable-line

  /* اعتبارسنجی سمت کلاینت — ترتیب بررسی‌ها مهم است */
  const validate = f => {
    if(Number(f.kind||0)===0)    return 'نوع تضمین مشخص نیست';
    if(Number(f.subject||0)===0) return 'موضوع تضمین مشخص نیست';
    if(Number(f.subject)===SUBJECT_PREPAYMENT && Number(f.prepayment_type||0)===0)
      return 'نوع پیش پرداخت مشخص نیست';
    if(!String(f.zemanat_no||'').trim()) return 'شماره ضمانتنامه باید مقدار داشته باشد';
    if(Number(f.amount||0)===0)  return 'مبلغ ضمانتنامه باید مقدار داشته باشد';
    if(!KINDS_WITHOUT_DATES.includes(Number(f.kind))){
      if(String(f.issu_date||'').length!==10) return 'تاریخ صدور ضمانتنامه باید مقدار معتبر داشته باشد';
      if(String(f.end_date||'').length!==10)  return 'تاریخ سررسید ضمانتنامه باید مقدار معتبر داشته باشد';
    }
    const cn=String(f.cancel_no||'').trim(), cd=String(f.cancel_date||'').trim();
    if(!!cn !== !!cd) return 'شماره یا تاریخ ابطال کامل نیست';
    const gn=String(f.give_no||'').trim(), gd=String(f.give_date||'').trim();
    if(!!gn !== !!gd) return 'شماره یا تاریخ ضبط کامل نیست';
    return null;
  };

  const handleSave = async()=>{
    const v = validate(form);
    if(v){ setError(v); return; }
    setError('');
    const url = modal==='new'    ? '/zemanats/create/'
              : modal==='cancel' ? `/zemanats/${selected.id}/cancel/`
              : modal==='give'   ? `/zemanats/${selected.id}/give/`
              : `/zemanats/${selected.id}/update/`;
    const body = modal==='cancel' ? {...form, cancel_reason:cancelReason} : form;
    const d = await API(url,{method:'POST',body:JSON.stringify(body)});
    if(d.ok){
      const msg = {new:'✅ ثبت شد', edit:'✅ ویرایش شد', cancel:'✅ ابطال ثبت شد', give:'✅ ضبط ثبت شد'}[modal];
      setModal(null); setCancelReason(null); fetchData(true); showToast(msg);
    } else setError(d.error||'خطا');
  };

  /* ── حذف — پنج مانع ── */
  const openDelete = ()=>{ if (!vbf.can('BTNDELETE')) return; if(!selected) return; setError(''); setModal('delete'); };
  const deleteBlockReason = r => {
    if(!r) return '';
    if(r.trans_count>0)   return 'برای این ضمانت نامه تمدید یا تقلیل ثبت شده و قابل حذف نیست';
    if(r.is_given)        return 'این ضمانتنامه قبلا ضبط شده و قابل حذف نیست';
    if(r.is_cancelled)    return 'این ضمانتنامه قبلا باطل شده و قابل حذف نیست';
    if(r.doc_id>0)        return 'برای این ضمانتنامه سند صادر شده و قابل حذف نیست';
    if(r.cancel_doc_id>0) return 'برای ابطال این ضمانتنامه سند صادر شده و قابل حذف نیست';
    if(r.give_doc_id>0)   return 'برای ضبط این ضمانتنامه سند صادر شده و قابل حذف نیست';
    return '';
  };
  const handleDelete = async()=>{
    const d = await API(`/zemanats/${selected.id}/delete/`,{method:'POST'});
    if(d.ok){ setModal(null); setSelected(null); fetchData(true); showToast('✅ حذف شد'); }
    else setError(d.error||'خطا');
  };

  /* ── تمدید و تقلیل ── */
  const transToForm = r => r ? {trans_id:r.id,new_amount:amountText(r.new_amount),new_end_date:r.new_end_date,
    receive_no:r.receive_no,receive_date:r.receive_date,send_no:r.send_no,send_date:r.send_date,remark:r.remark,
    recipient1:r.recipient1,send_no1:r.send_no1,send_date1:r.send_date1,
    recipient2:r.recipient2,send_no2:r.send_no2,send_date2:r.send_date2,
    recipient3:r.recipient3,send_no3:r.send_no3,send_date3:r.send_date3,
    recipient4:r.recipient4,send_no4:r.send_no4,send_date4:r.send_date4} : EMPTY_TRANS;
  const loadTrans = async (keepId=0)=>{
    const d = await API(`/zemanats/${selected.id}/transactions/`);
    if(!d.ok) return d;
    setTrans(d.rows);
    const r = d.rows.find(x=>x.id===keepId) || d.rows[0] || null;
    setTransSel(r); setTransForm(transToForm(r));
    return d;
  };
  const openTrans = async()=>{
    if(!selected) return;
    setTransError(''); setTransMode('view');
    const d = await loadTrans();
    if(d.ok) setModal('trans'); else alert(d.error||'خطا');
  };
  const pickTrans = r => { if(transMode!=='view') return; setTransSel(r); setTransForm(transToForm(r)); setTransError(''); };
  const newTrans = ()=>{ if(!vbf_zemanattransaction.can('BTNNEW')) return;
    setTransForm(EMPTY_TRANS); setTransError(''); setTransMode('new'); };
  const editTrans = ()=>{ if(!vbf_zemanattransaction.can('BTNEDIT') || !transSel) return;
    if(transSel.has_document){ setTransError('برای این رکورد سند صادر شده و قابل ویرایش نیست'); return; }
    setTransError(''); setTransMode('edit'); };
  const cancelTrans = ()=>{ setTransMode('view'); setTransError(''); setTransForm(transToForm(transSel)); };
  const saveTrans = async()=>{
    if(!vbf_zemanattransaction.can('BTNOK')) return;
    if(!String(transForm.send_no||'').trim()){ setTransError('شماره نامه ارسالی وارد نشده است'); return; }
    setTransError('');
    const body = JSON.stringify({...transForm,new_amount:amountValue(transForm.new_amount)});
    const d = transMode==='edit'
      ? await API(`/transactions/${transForm.trans_id}/update/`,{method:'POST',body})
      : await API(`/zemanats/${selected.id}/transactions/save/`,{method:'POST',body});
    if(d.ok){
      setTransMode('view'); await loadTrans(d.id); fetchData(true); showToast('✅ ثبت شد');
    } else setTransError(d.error||'خطا');
  };
  const deleteTrans = async()=>{
    if(!vbf_zemanattransaction.can('BTNDELETE') || !transSel) return;
    if(transSel.has_document){ setTransError('برای این رکورد سند صادر شده و قابل حذف نیست'); return; }
    if(!window.confirm('این تمدید/تقلیل حذف شود؟')) return;
    const d = await API(`/transactions/${transSel.id}/delete/`,{method:'POST'});
    if(d.ok){ await loadTrans(); fetchData(true); showToast('✅ حذف شد'); }
    else setTransError(d.error||'خطا');
  };
  const tf = k => ({value:transForm[k], readOnly:transMode==='view',
    onChange:e=>setTransForm(p=>({...p,[k]:e.target.value}))});
  const tDate = k => ({value:transForm[k], onChange:v=>{ if(transMode!=='view') setTransForm(p=>({...p,[k]:v})); }});

  const doLookup = async q => {
    const d = await API(`/contracts/lookup/?q=${encodeURIComponent(q||'')}`);
    if(!d.ok) return;
    // اگر فقط یک قرارداد پیدا شد همان انتخاب می‌شود
    if(q && d.rows.length===1){ pickContract(d.rows[0]); return; }
    setLookup(d.rows);
  };
  const pickContract = r => {
    setForm(p=>({...p,contract_id:r.id,contract_no:r.contract_no,contract_date:r.contract_date,
      contractor:r.contractor,contractor_code:r.contractor_code}));
    setContractQ(''); setLookup([]);
  };
  const clearContract = () => {
    setForm(p=>({...p,contract_id:0,contract_no:'',contract_date:'',contractor:'',contractor_code:0}));
    setContractQ(''); setLookup([]);
  };
  const onContractType = v => {
    // با تایپ دوباره، قرارداد قبلی کنار می‌رود و متن فقط عبارت جستجوست
    if(form.contract_id) setForm(p=>({...p,contract_id:0,contract_no:'',contract_date:'',
      contractor:'',contractor_code:0}));
    setContractQ(v);
    clearTimeout(contractTimer.current);
    contractTimer.current = setTimeout(()=>doLookup(v.trim()), 350);
  };
  const contractLabel = form.contract_id
    ? [toFa(form.contract_no), toFa(form.contract_date)].filter(Boolean).join(' - ')
      + (form.contractor?`  (${form.contractor})`:'')
    : contractQ;
  const doBranchLookup = async q => {
    const d = await API(`/branches/lookup/?q=${encodeURIComponent(q||'')}`);
    if(d.ok) setBranchLookup(d.rows);
  };
  const doGiveBranchLookup = async q => {
    const d = await API(`/branches/lookup/?q=${encodeURIComponent(q||'')}`);
    if(d.ok) setGiveBranchLookup(d.rows);
  };

  const kindOpts    = [{value:'',label:'همه انواع'},
    ...meta.kinds.map(k=>({value:k.code,label:k.title}))];
  const subjectOpts = [{value:'',label:'همه موضوعات'},
    ...meta.subjects.map(k=>({value:k.code,label:k.title}))];
  const contractorOpts = [{value:0,label:'همه پیمانکاران'},
    ...meta.contractors.map(c=>({value:c.code,label:c.title}))];

  const isProperty = Number(form.kind)===KIND_PROPERTY;
  const needsDates = !KINDS_WITHOUT_DATES.includes(Number(form.kind));
  const isPrepay   = Number(form.subject)===SUBJECT_PREPAYMENT;

  const TABS = [
    {id:'main',     label:'مشخصات اصلی'},
    {id:'validity', label:'اعتبار و درخواست'},
    {id:'cancel',   label:'ابطال'},
    {id:'give',     label:'ضبط'},
  ];

  return (
    <div style={s.page}>
      <header style={s.header}>
        <div style={s.hLeft}>
          <button onClick={onBack} style={s.backBtn}>← بازگشت</button>
          <div style={s.hLogo}><img src={require('../../assets/images/arjan.png')} alt="" style={{width:'34px',height:'34px',borderRadius:'7px'}}/></div>
          <div><div style={s.hTitle}>ضمانتنامه</div><div style={s.hSub}>تضمینات — بخش ضمانتنامه</div></div>
        </div>
        <span style={s.userName}>{user?.full_name}</span>
      </header>
      <TabBar/>

      <main style={s.main}>
        {/* ── نوار ابزار ── */}
        <div style={s.toolbar}>
          <div style={s.toolBtns}>
            {vbf.can('BTNNEW') && <button onClick={openNew} style={s.btnNew}>＋ جدید</button>}
            {vbf.can('BTNEDIT') && <button onClick={openEdit} disabled={!selected} style={{...s.btnEdit,opacity:selected?1:.4}}>✎ ویرایش</button>}
            {vbf.can('BTNDELETE') && <button onClick={openDelete} disabled={!selected} style={{...s.btnDel,opacity:selected?1:.4}}>✕ حذف</button>}
            {vbf.can('BTNSETCANCLE') && !rowInfo.cancel_blocked &&
              <button onClick={openCancel} disabled={!selected} style={{...s.btnCancelOp,opacity:selected?1:.4}}>⊘ ابطال</button>}
            {vbf.can('BTNSETGIVE') &&
              <button onClick={openGive} disabled={!selected} style={{...s.btnGiveOp,opacity:selected?1:.4}}>⚑ ضبط</button>}
            <button onClick={openTrans} disabled={!selected} style={{...s.btnTrans,opacity:selected?1:.4}}>
              🔁 تمدیدها و تقلیل‌ها{selected?.trans_count>0?` (${toFa(selected.trans_count)})`:''}</button>
          </div>
        </div>

        {/* ── فیلترها + جستجو ── */}
        <div style={s.toolbar2}>
          <div style={{width:'190px'}}>
            <FSelect label="نوع تضمین" name="kind" value={kind}
              onChange={e=>setKind(e.target.value)} options={kindOpts}/>
          </div>
          <div style={{width:'180px'}}>
            <FSelect label="موضوع تضمین" name="subject" value={subject}
              onChange={e=>setSubject(e.target.value)} options={subjectOpts}/>
          </div>
          <div style={{width:'240px'}}>
            <FSelect label="پیمانکار" name="contractor" value={contractor}
              onChange={e=>setContractor(Number(e.target.value))} options={contractorOpts}/>
          </div>
          <div style={{display:'flex',gap:'12px',alignItems:'center',paddingBottom:'6px'}}>
            {STATES.map(m=>(
              <label key={m.val} style={s.radio}>
                <input type="radio" name="state" checked={state===m.val} onChange={()=>setState(m.val)}/>
                <span>{m.label}</span></label>))}
          </div>
          <div style={{marginRight:'auto'}}>
            <div style={s.fL}>جستجو</div>
            <input value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="شماره / سپام / پیمانکار / قرارداد" style={{...s.fI,width:'260px'}}/>
          </div>
        </div>

        {/* وضعیت بدهی پیش‌پرداخت قرارداد برای ضمانتنامهٔ پیش‌پرداخت انتخاب‌شده */}
        {selected && !hidePrepayMsg && rowInfo.prepayment_message && prepayMsgFor===selected.id && (
          <div style={{...s.infoBox,marginBottom:'8px',display:'flex',alignItems:'center',gap:'10px'}}>
            <span style={{flex:1}}>ℹ️ {rowInfo.prepayment_message}</span>
            <button onClick={()=>setPrepayMsgFor(0)} style={s.btnMini}>متوجه شدم</button>
            <button onClick={()=>setHidePrepayMsg(true)} style={s.btnMini}>دیگر این پیام نشان داده نشود</button>
          </div>)}

        {/* تمدید/تقلیل‌هایی که با مبلغ یا سررسید خالی ثبت شده بودند و سند خورده‌اند */}
        {meta.trans_repair?.with_document?.length>0 && (
          <div style={{background:'#fff3e0',border:'1px solid #ffb74d',borderRadius:'8px',padding:'8px 12px',
            margin:'0 0 8px',fontSize:'12px',color:'#e65100'}}>
            ⚠️ برای این تمدید/تقلیل‌ها سند حسابداری صادر شده ولی مبلغ یا سررسید جدیدشان ناقص ثبت شده است؛
            سند هر کدام را در تنظیم حساب بررسی کنید:
            {' '}{meta.trans_repair.with_document.map(r=>
              `ضمانتنامه ${toFa(r.zemanat_no)} — سند ${toFa(r.document_no)} (${toFa(r.document_date)})`).join('  |  ')}
          </div>)}

        {/* ── گرید اصلی ── */}
        <div style={s.tableWrap}>
          <table style={{width:'100%',borderCollapse:'collapse',tableLayout:'fixed'}}>
            <thead style={{position:'sticky',top:0,zIndex:3}}>
              <tr>
                <TH idx={0}>ردیف</TH>
                <TH idx={1}>سریال</TH>
                <TH idx={2}>شماره</TH>
                <TH idx={3}>سپام</TH>
                <TH idx={4}>نوع تضمین</TH>
                <TH idx={5}>موضوع تضمین</TH>
                <TH idx={6}>قرارداد</TH>
                <TH idx={7} right>موضوع قرارداد</TH>
                <TH idx={8} right>پیمانکار</TH>
                <TH idx={9} gold>مبلغ</TH>
                <TH idx={10} gold>مبلغ جاری</TH>
                <TH idx={11}>صدور</TH>
                <TH idx={12}>سررسید</TH>
                <TH idx={13}>ابطال</TH>
                <TH idx={14}>ضبط</TH>
                <TH idx={15}>وضعیت</TH>
                <TH idx={16}>تمدید/تقلیل</TH>
                <TH idx={17} right>توضیح</TH>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length===0 && (
                <tr><td colSpan={18} style={s.empty}>
                  {loading?'در حال بارگذاری...':'اطلاعاتی برای نمایش وجود ندارد'}
                </td></tr>)}
              {visibleRows.map((r,idx)=>{
                const sel = selected?.id===r.id;
                const bg  = sel ? '#64b5f6'
                          : r.is_given ? '#ffebee'
                          : r.is_cancelled ? '#eceff1'
                          : idx%2===0 ? '#fff' : '#fdf6e3';
                return (
                  <tr key={r.id} onClick={()=>setSelected(r)} style={{cursor:'pointer',background:bg}}
                    onMouseEnter={e=>{if(!sel)e.currentTarget.style.background='#bbdefb';}}
                    onMouseLeave={e=>{if(!sel)e.currentTarget.style.background=bg;}}>
                    <td style={s.td}>{toFa(idx+1)}</td>
                    <td style={s.td}>{toFa(r.serial_no)}</td>
                    <td style={{...s.td,fontWeight:'700',direction:'ltr'}}>{toFa(r.zemanat_no)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.sepam)}</td>
                    <td style={s.td}>{r.kind_title}</td>
                    <td style={s.td}>{r.subject_title}
                      {r.prepayment_title?` (${r.prepayment_title})`:''}</td>
                    <td style={s.td}>{toFa(r.contract_no)}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contract_subject}>{r.contract_subject}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.contractor}>{r.contractor}</td>
                    <td style={{...s.td,...s.num,color:'#1b5e20'}}>{numFmt(r.amount)}</td>
                    <td style={{...s.td,...s.num,
                      color:r.current_amount!==r.amount?'#e65100':'#999'}}>{numFmt(r.current_amount)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.issu_date)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.current_end_date)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.cancel_date)}</td>
                    <td style={{...s.td,direction:'ltr'}}>{toFa(r.give_date)}</td>
                    <td style={s.td}>
                      {r.is_given   ? <span style={{color:'#c62828',fontWeight:'700'}}>ضبط شده</span>
                       :r.is_cancelled? <span style={{color:'#607d8b',fontWeight:'700'}}>باطل شده</span>
                       : <span style={{color:'#1b5e20',fontWeight:'700'}}>معتبر</span>}
                    </td>
                    <td style={s.td}>{r.trans_count?toFa(r.trans_count):''}</td>
                    <td style={{...s.td,textAlign:'right'}} title={r.remark}>{r.remark}</td>
                  </tr>);
              })}
            </tbody>
          </table>
        </div>
        <LoadMoreButton onClick={()=>fetchData(false)} loading={loading}/>

        <div style={s.summary}>
          <span>تعداد: <b>{toFa(visibleRows.length)}</b> از <b>{toFa(totalCount)}</b></span>
          <span>جمع مبلغ: <b style={{color:'#1b5e20'}}>{numFmt(sumAmount)||'۰'}</b> ریال</span>
          {meta.stats?.count>0 && <>
            <span>کل: <b>{toFa(meta.stats.count)}</b></span>
            <span>باطل شده: <b style={{color:'#607d8b'}}>{toFa(meta.stats.cancelled)}</b></span>
            <span>ضبط شده: <b style={{color:'#c62828'}}>{toFa(meta.stats.given)}</b></span>
          </>}
          {selected && <span>انتخابی: <b>{toFa(selected.zemanat_no)}</b> — {selected.status}</span>}
          {selected && rowInfo.black_list && <span style={{color:'#c62828',fontWeight:'700'}}>⛔ {rowInfo.black_list}</span>}
        </div>
      </main>

      {/* ══ مودال جدید / ویرایش ══ */}
      {['new','edit','cancel','give'].includes(modal) && (
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'880px'}} onClick={e=>e.stopPropagation()}>
            <div style={s.mHeader}>
              <div style={s.mTitle}>{{new:'➕ ثبت ضمانتنامه جدید', edit:'✎ ویرایش ضمانتنامه',
                cancel:'⊘ ابطال ضمانتنامه', give:'⚑ ضبط ضمانتنامه'}[modal]}
                {modal!=='new' && selected ? ` — ${toFa(selected.zemanat_no)}` : ''}</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button>
            </div>
            {/* تب‌های داخلی مودال */}
            <div style={s.mTabs}>
              {TABS.map(t=>(
                <button key={t.id} onClick={()=>setTab(t.id)}
                  style={{...s.mTab,...(tab===t.id?s.mTabActive:{})}}>{t.label}</button>))}
            </div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              {modal!=='new' && editLock?.black && (
                <div style={{...s.errBox,marginBottom:'8px'}}>⛔ {editLock.black}</div>)}
              {modal!=='new' && editLock?.messages?.length>0 && (
                <div style={s.warnBox}>
                  {editLock.messages.map((m,k)=><div key={k}>🔒 {m}</div>)}
                  <div style={{marginTop:'4px',fontWeight:'400'}}>
                    فقط فیلدهای سفید قابل تغییرند؛ بقیه همان مقدار ثبت‌شده را نگه می‌دارند.</div>
                </div>)}
              {modal==='cancel' && cancelReason && (
                <div style={s.infoBox}>
                  علت ابطال: <b>{cancelReason.title}</b> — نامه شماره {toFa(cancelReason.letter_no)} تاریخ {toFa(cancelReason.letter_date)}
                  {cancelReason.remark?` — ${cancelReason.remark}`:''}
                  <div style={{fontSize:'11px'}}>با ثبت ابطال، این نامه در قرارداد و متن علت در «توضیح ثابت» ضمانتنامه ثبت می‌شود.</div>
                </div>)}
              {modal!=='new' && form.fix_remark && (
                <div style={{...s.infoBox,marginBottom:'8px'}}>توضیح ثابت: {form.fix_remark}</div>)}

              {tab==='main' && <>
                <div style={s.row}>
                  <FSelect label="نوع تضمین *" name="kind" value={form.kind} disabled={ro('kind')}
                    onChange={e=>setForm(p=>({...p,kind:Number(e.target.value)}))}
                    options={meta.kinds.map(k=>({value:k.code,label:k.title}))} flex="0 0 32%"/>
                  <FSelect label="موضوع تضمین *" name="subject" value={form.subject} disabled={ro('subject')}
                    onChange={e=>setForm(p=>({...p,subject:Number(e.target.value),
                      prepayment_type:Number(e.target.value)===SUBJECT_PREPAYMENT?p.prepayment_type:0}))}
                    options={[{value:0,label:'— انتخاب کنید —'},
                      ...meta.subjects.map(k=>({value:k.code,label:k.title}))]} flex="0 0 32%"/>
                  {isPrepay && <FSelect label="نوع پیش پرداخت *" name="prepayment_type"
                    value={form.prepayment_type} disabled={ro('prepayment_type')}
                    onChange={e=>setForm(p=>({...p,prepayment_type:Number(e.target.value)}))}
                    options={[{value:0,label:'— انتخاب کنید —'},
                      ...meta.prepayments.map(k=>({value:k.code,label:k.title}))]} flex="0 0 32%"/>}
                </div>
                <div style={s.row}>
                  <FInput label="شماره ضمانتنامه *" name="zemanat_no" readOnly={ro('zemanat_no')} value={form.zemanat_no}
                    onChange={handleChange} dir="ltr" flex="0 0 24%"/>
                  <FInput label="شماره سپام" name="sepam" readOnly={ro('sepam')} value={form.sepam}
                    onChange={handleChange} dir="ltr" flex="0 0 24%"/>
                  <FInput label="مبلغ ضمانتنامه *" name="amount" readOnly={ro('amount')} value={form.amount}
                    onChange={handleChange} type="number" dir="ltr" flex="0 0 28%"/>
                  {isProperty && <FInput label="ارزش ملک" name="property_value" readOnly={ro('property_value')}
                    value={form.property_value} onChange={handleChange} type="number" dir="ltr"
                    flex="0 0 20%"/>}
                </div>
                <div style={s.row}>
                  <div style={{flex:'0 0 62%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>قرارداد</label>
                    <input value={contractLabel} dir="rtl" lang="fa" readOnly={ro('contract_id')}
                      placeholder="شماره، موضوع قرارداد یا نام پیمانکار..."
                      onChange={e=>{ if(!ro('contract_id')) onContractType(e.target.value); }}
                      onKeyDown={e=>{ if(ro('contract_id')) return;
                                      if(e.key==='Delete'){ e.preventDefault(); clearContract(); }
                                      if(e.key==='Escape') setLookup([]); }}
                      onFocus={()=>{ if(!ro('contract_id') && !form.contract_id) doLookup(contractQ.trim()); }}
                      onBlur={()=>setTimeout(()=>setLookup([]),200)}
                      style={{...s.fI,background:ro('contract_id')?'#f5f5f5':'#fff'}}/>
                    {lookup.length>0&&(
                      <div style={s.dropdown}>{lookup.map(r=>(
                        <div key={r.id} onMouseDown={e=>{e.preventDefault();pickContract(r);}}
                          style={s.dropItem}>
                          <b>{toFa(r.contract_no)}</b> - {toFa(r.contract_date)} — {r.contractor}
                          <div style={{fontSize:'11px',color:'#888'}}>{r.subject}</div>
                        </div>))}</div>)}
                  </div>
                  <FSelect label="موضوع ضمانتنامه" name="zemanat_subject_id"
                    value={form.zemanat_subject_id} disabled={ro('zemanat_subject_id')}
                    onChange={e=>setForm(p=>({...p,zemanat_subject_id:Number(e.target.value)}))}
                    options={[{value:0,label:'—'},
                      ...subjectList.map(x=>({value:x.id,label:x.title}))]} flex="0 0 36%"/>
                </div>
                <div style={s.row}>
                  <div style={{flex:'0 0 40%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>شعبه بانک</label>
                    <input value={form.branch||(form.branch_code?toFa(form.branch_code):'')}
                      placeholder="نام یا کد شعبه..." readOnly={ro('branch_code')}
                      onChange={e=>{ if(ro('branch_code')) return;
                        setForm(p=>({...p,branch:e.target.value,branch_code:0}));doBranchLookup(e.target.value);}}
                      onFocus={()=>{ if(!ro('branch_code')) doBranchLookup(''); }}
                      onBlur={()=>setTimeout(()=>setBranchLookup([]),200)}
                      style={{...s.fI,background:ro('branch_code')?'#f5f5f5':'#fff'}}/>
                    {branchLookup.length>0&&(
                      <div style={s.dropdown}>{branchLookup.map(r=>(
                        <div key={r.code} onMouseDown={e=>{e.preventDefault();setForm(p=>({...p,branch_code:r.code,branch:r.title}));setBranchLookup([]);}}
                          style={s.dropItem}>{r.title}</div>))}</div>)}
                  </div>
                  {needsDates && <>
                    <FDate label="تاریخ صدور *" value={form.issu_date} disabled={ro('issu_date')}
                      onChange={v=>setForm(p=>({...p,issu_date:v}))} flex="0 0 28%"/>
                    <FDate label="تاریخ سررسید *" value={form.end_date} disabled={ro('end_date')}
                      onChange={v=>setForm(p=>({...p,end_date:v}))} flex="0 0 28%"/>
                  </>}
                  <FCheck label="مناقصه" checked={Number(form.monaghese)===1} disabled={ro('monaghese')}
                    onChange={e=>setForm(p=>({...p,monaghese:e.target.checked?1:0}))} flex="0 0 15%"/>
                </div>
                <div style={s.row}>
                  <FInput label="شماره پرونده" name="case_no" readOnly={ro('case_no')} value={form.case_no}
                    onChange={handleChange} flex="0 0 32%"/>
                  <FInput label="توضیح" name="remark" readOnly={ro('remark')} value={form.remark}
                    onChange={handleChange} flex="0 0 66%"/>
                </div>
                {!needsDates && <div style={s.infoBox}>
                  برای «{meta.kinds.find(k=>k.code===Number(form.kind))?.title}» تاریخ صدور و
                  سررسید اجباری نیست.</div>}
              </>}

              {tab==='validity' && <>
                <div style={s.row}>
                  <FInput label="درخواست اعتبار: شماره" name="request_validity_no" readOnly={ro('request_validity_no')}
                    value={form.request_validity_no} onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ درخواست اعتبار" value={form.request_validity_date} disabled={ro('request_validity_date')}
                    onChange={v=>setForm(p=>({...p,request_validity_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <FInput label="تاییدیه بانک: شماره" name="validity_no" readOnly={ro('validity_no')} value={form.validity_no}
                    onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ تاییدیه بانک" value={form.validity_date} disabled={ro('validity_date')}
                    onChange={v=>setForm(p=>({...p,validity_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <FInput label="استعلام: شماره" name="request_no" readOnly={ro('request_no')} value={form.request_no}
                    onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ استعلام" value={form.request_date} disabled={ro('request_date')}
                    onChange={v=>setForm(p=>({...p,request_date:v}))} flex="0 0 32%"/>
                  <FDate label="تاریخ ثبت" value={form.save_date} disabled={ro('save_date')}
                    onChange={v=>setForm(p=>({...p,save_date:v}))} flex="0 0 32%"/>
                </div>
              </>}

              {tab==='cancel' && <>
                <div style={s.warnBox}>
                  شماره و تاریخ ابطال باید با هم پر یا با هم خالی باشند.
                </div>
                <div style={s.row}>
                  <FInput label="شماره ابطال" name="cancel_no" readOnly={ro('cancel_no')} value={form.cancel_no}
                    onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ ابطال" value={form.cancel_date} disabled={ro('cancel_date')}
                    onChange={v=>setForm(p=>({...p,cancel_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <FInput label="نامه ابطال بانک: شماره" name="cancel_bank_no" readOnly={ro('cancel_bank_no')}
                    value={form.cancel_bank_no} onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ نامه ابطال بانک" value={form.cancel_bank_date} disabled={ro('cancel_bank_date')}
                    onChange={v=>setForm(p=>({...p,cancel_bank_date:v}))} flex="0 0 32%"/>
                </div>
                {selected?.release_id>0 && modal==='edit' && (
                  <div style={s.warnBox}>
                    ⚠️ این ضمانتنامه در قبال یک صورت وضعیت باطل شده است.
                    اگر شماره ابطال را خالی کنید، اتصال به صورت وضعیت هم برداشته می‌شود.
                  </div>)}
              </>}

              {tab==='give' && <>
                <div style={s.warnBox}>
                  شماره و تاریخ ضبط باید با هم پر یا با هم خالی باشند.
                </div>
                <div style={s.row}>
                  <FInput label="شماره ضبط" name="give_no" readOnly={ro('give_no')} value={form.give_no}
                    onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ ضبط" value={form.give_date} disabled={ro('give_date')}
                    onChange={v=>setForm(p=>({...p,give_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <FInput label="درخواست ضبط: شماره" name="give_request_no" readOnly={ro('give_request_no')}
                    value={form.give_request_no} onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ درخواست ضبط" value={form.give_request_date} disabled={ro('give_request_date')}
                    onChange={v=>setForm(p=>({...p,give_request_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <FInput label="نامه ضبط بانک: شماره" name="give_bank_no" readOnly={ro('give_bank_no')}
                    value={form.give_bank_no} onChange={handleChange} flex="0 0 32%"/>
                  <FDate label="تاریخ نامه ضبط بانک" value={form.give_bank_date} disabled={ro('give_bank_date')}
                    onChange={v=>setForm(p=>({...p,give_bank_date:v}))} flex="0 0 32%"/>
                </div>
                <div style={s.row}>
                  <div style={{flex:'0 0 48%',display:'flex',flexDirection:'column',gap:'4px',position:'relative'}}>
                    <label style={{fontSize:'12px',fontWeight:'600',color:'#555'}}>بانک حساب ضبط</label>
                    <input value={form.give_branch||''} placeholder="نام بانک یا شعبه..." readOnly={ro('give_branch_code')}
                      onChange={e=>{ if(ro('give_branch_code')) return;
                        setForm(p=>({...p,give_branch:e.target.value,give_branch_code:0}));doGiveBranchLookup(e.target.value);}}
                      onKeyDown={e=>{ if(!ro('give_branch_code') && e.key==='Delete'){ e.preventDefault();
                        setForm(p=>({...p,give_branch:'',give_branch_code:0})); } }}
                      onFocus={()=>{ if(!ro('give_branch_code')) doGiveBranchLookup(''); }}
                      onBlur={()=>setTimeout(()=>setGiveBranchLookup([]),200)}
                      style={{...s.fI,background:ro('give_branch_code')?'#f5f5f5':'#fff'}}/>
                    {giveBranchLookup.length>0&&(
                      <div style={s.dropdown}>{giveBranchLookup.map(r=>(
                        <div key={r.code} onMouseDown={e=>{e.preventDefault();
                          setForm(p=>({...p,give_branch_code:r.code,give_branch:r.title}));setGiveBranchLookup([]);}}
                          style={s.dropItem}>{r.title}</div>))}</div>)}
                  </div>
                  <FInput label="حساب واریزی ضبط" name="give_account_no" readOnly={ro('give_account_no')}
                    value={form.give_account_no} onChange={handleChange} dir="ltr" flex="0 0 48%"/>
                </div>
              </>}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {vbf.can('BTNOK') && <button onClick={handleSave} style={s.btnSave}>ثبت</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ علت ابطال ══ */}
      {reasonBox && (
        <div style={s.overlay}>
          <div style={{...s.modal,maxWidth:'560px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#6a1b9a,#8e24aa)'}}>
              <div style={s.mTitle}>{reasonBox.title || 'علت ابطال ضمانتنامه را مشخص کنید'}</div>
              <button onClick={()=>setReasonBox(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {reasonBox.error && <div style={s.errBox}>{reasonBox.error}</div>}
              {!reasonBox.title ? (
                <div style={{display:'flex',flexWrap:'wrap',gap:'8px'}}>
                  {reasonBox.reasons.map(r=>(
                    <button key={r} onClick={()=>setReasonBox(p=>({...p,title:r,error:''}))}
                      style={{...s.btnSave,background:'#6a1b9a'}}>{r}</button>))}
                </div>
              ) : <>
                <div style={s.row}>
                  <FInput label="شماره نامه" name="letter_no" value={reasonBox.letter_no}
                    onChange={e=>setReasonBox(p=>({...p,letter_no:e.target.value}))} flex="0 0 48%"/>
                  <FDate label="تاریخ" value={reasonBox.letter_date}
                    onChange={v=>setReasonBox(p=>({...p,letter_date:v}))} flex="0 0 48%"/>
                </div>
                <div style={s.row}>
                  <FInput label={reasonBox.need_remark.includes(reasonBox.title)?'توضیح * (دست‌کم ۱۰ حرف)':'توضیح'}
                    name="remark" value={reasonBox.remark}
                    onChange={e=>setReasonBox(p=>({...p,remark:e.target.value}))} flex="1"/>
                </div>
              </>}
            </div>
            <div style={s.mFooter}>
              {reasonBox.title
                ? <button onClick={()=>setReasonBox(p=>({...p,title:'',error:''}))} style={s.btnCancel}>تغییر علت</button>
                : null}
              <button onClick={()=>setReasonBox(null)} style={s.btnCancel}>لغو</button>
              {reasonBox.title && <button onClick={confirmReason} style={s.btnSave}>ادامه</button>}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال حذف ══ */}
      {modal==='delete'&&selected&&(
        <div style={s.overlay} onClick={()=>setModal(null)}>
          <div style={{...s.modal,maxWidth:'520px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>
              <div style={s.mTitle}>⚠️ تأیید حذف</div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {error&&<div style={s.errBox}>{error}</div>}
              <p style={{fontSize:'14px',lineHeight:'2'}}>
                آیا از حذف ضمانتنامه شماره <b>{toFa(selected.zemanat_no)}</b> ({selected.kind_title})
                {' '}به مبلغ <b>{numFmt(selected.amount)}</b> ریال مطمئن هستید؟</p>
              {deleteBlockReason(selected) &&
                <p style={{color:'#c62828',fontWeight:'700'}}>⚠️ {deleteBlockReason(selected)}</p>}
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>setModal(null)} style={s.btnCancel}>لغو</button>
              {!deleteBlockReason(selected) && (vbf.can('BTNDELETE') && <button onClick={handleDelete}
                style={{...s.btnSave,background:'linear-gradient(135deg,#b71c1c,#c62828)'}}>حذف</button>)}
            </div>
          </div>
        </div>
      )}

      {/* ══ مودال تمدید و تقلیل ══ */}
      {modal==='trans'&&selected&&(
        <div style={s.overlay} onClick={()=>{ if(transMode==='view') setModal(null); }}>
          <div style={{...s.modal,maxWidth:'1100px'}} onClick={e=>e.stopPropagation()}>
            <div style={{...s.mHeader,background:'linear-gradient(135deg,#00695c,#00897b)'}}>
              <div style={s.mTitle}>
                🔁 تمدیدها و تقلیل‌ها — ضمانتنامه {toFa(selected.zemanat_no)}
                {' '}| مبلغ اولیه {numFmt(selected.amount)} | آخرین مبلغ {numFmt(selected.current_amount)}
                {' '}| آخرین سررسید {toFa(selected.current_end_date)}
              </div>
              <button onClick={()=>setModal(null)} style={s.closeBtn}>✕</button></div>
            <div style={s.mBody}>
              {transError&&<div style={s.errBox}>{transError}</div>}

              {/* فرم */}
              <div style={s.transForm}>
                <div style={s.row}>
                  <FInput label="مبلغ جدید" name="new_amount" {...tf('new_amount')}
                    onChange={e=>setTransForm(p=>({...p,new_amount:amountText(e.target.value)}))}
                    dir="ltr" flex="0 0 24%"/>
                  <FDate label="سررسید جدید" {...tDate('new_end_date')} flex="0 0 24%"/>
                  <FInput label="سند" name="doc" value={transSel&&transMode==='view'&&transSel.has_document
                    ?`${toFa(transSel.document_no)} — ${toFa(transSel.document_date)}`:''} readOnly flex="0 0 24%"/>
                </div>
                {transMode!=='view' && <div style={{fontSize:'11px',color:'#00695c',margin:'-4px 0 8px'}}>
                  اگر مبلغ یا سررسید خالی بماند، آخرین مبلغ و سررسید ضمانتنامه ثبت می‌شود.</div>}
                <div style={s.row}>
                  <FInput label="نامه ارسالی: شماره *" name="send_no" {...tf('send_no')} flex="0 0 24%"/>
                  <FDate label="تاریخ" {...tDate('send_date')} flex="0 0 24%"/>
                  <FInput label="نامه دریافتی: شماره" name="receive_no" {...tf('receive_no')} flex="0 0 24%"/>
                  <FDate label="تاریخ" {...tDate('receive_date')} flex="0 0 24%"/>
                </div>
                <div style={s.row}>
                  <FInput label="توضیحات" name="remark" {...tf('remark')} flex="1"/>
                </div>
                <div style={{display:'flex',gap:'12px',flexWrap:'wrap'}}>
                  <fieldset style={s.fset}><legend style={s.legend}>پیگیری</legend>
                    {[1,2,3].map(n=>(
                      <div key={n} style={s.row}>
                        <FSelect label={['','پیگیری اول','پیگیری دوم','پیگیری سوم'][n]+' — مخاطب'}
                          name={`recipient${n}`} value={transForm[`recipient${n}`]}
                          onChange={e=>{ if(transMode!=='view') setTransForm(p=>({...p,[`recipient${n}`]:Number(e.target.value)})); }}
                          options={RECIPIENTS.map((x,k)=>({value:k,label:x}))} flex="0 0 34%"/>
                        <FInput label="شماره" name={`send_no${n}`} {...tf(`send_no${n}`)} flex="0 0 30%"/>
                        <FDate label="تاریخ" {...tDate(`send_date${n}`)} flex="0 0 32%"/>
                      </div>))}
                  </fieldset>
                  <fieldset style={{...s.fset,flex:'0 0 32%'}}><legend style={s.legend}>ضبط بعلت عدم تمدید</legend>
                    <div style={s.row}>
                      <FSelect label="مخاطب" name="recipient4" value={transForm.recipient4}
                        onChange={e=>{ if(transMode!=='view') setTransForm(p=>({...p,recipient4:Number(e.target.value)})); }}
                        options={RECIPIENTS.map((x,k)=>({value:k,label:x}))} flex="1"/>
                    </div>
                    <div style={s.row}>
                      <FInput label="شماره" name="send_no4" {...tf('send_no4')} flex="0 0 48%"/>
                      <FDate label="تاریخ" {...tDate('send_date4')} flex="0 0 48%"/>
                    </div>
                  </fieldset>
                </div>
                <div style={{display:'flex',gap:'8px',justifyContent:'flex-end',padding:'10px 0'}}>
                  {transMode==='view' ? <>
                    {vbf_zemanattransaction.can('BTNNEW') &&
                      <button onClick={newTrans} style={s.btnSave}>＋ جدید</button>}
                    {vbf_zemanattransaction.can('BTNEDIT') &&
                      <button onClick={editTrans} disabled={!transSel}
                        style={{...s.btnSave,background:'#1565c0',opacity:transSel?1:.4}}>✎ ویرایش</button>}
                    {vbf_zemanattransaction.can('BTNDELETE') &&
                      <button onClick={deleteTrans} disabled={!transSel}
                        style={{...s.btnSave,background:'#c62828',opacity:transSel?1:.4}}>✕ حذف</button>}
                  </> : <>
                    {vbf_zemanattransaction.can('BTNOK') &&
                      <button onClick={saveTrans} style={s.btnSave}>ثبت</button>}
                    {vbf_zemanattransaction.can('BTNCANCEL') &&
                      <button onClick={cancelTrans} style={s.btnCancel}>لغو</button>}
                  </>}
                </div>
              </div>

              {/* فهرست */}
              <div style={{overflow:'auto',maxHeight:'32vh',marginTop:'8px'}}>
                <table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead><tr>
                    {['نامه ارسالی','تاریخ نامه ارسالی','نامه دریافتی','تاریخ نامه دریافتی','مبلغ جدید',
                      'سررسید جدید','توضیحات','پیگیری اول','پیگیری دوم','پیگیری سوم','ضبط بعلت عدم تمدید','سند']
                      .map(h=><th key={h} style={s.subTh}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {trans.length===0&&<tr><td colSpan={12} style={s.empty}>
                      تمدید یا تقلیلی ثبت نشده است</td></tr>}
                    {trans.map((r,idx)=>(
                      <tr key={r.id} onClick={()=>pickTrans(r)}
                        style={{cursor:transMode==='view'?'pointer':'default',
                          background:transSel?.id===r.id?'#b2dfdb':idx%2===0?'#fff':'#f5f5dc'}}>
                        <td style={s.subTd}>{toFa(r.send_no)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.send_date)}</td>
                        <td style={s.subTd}>{toFa(r.receive_no)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.receive_date)}</td>
                        <td style={{...s.subTd,...s.num,color:'#e65100'}}>{numFmt(r.new_amount)}</td>
                        <td style={{...s.subTd,direction:'ltr'}}>{toFa(r.new_end_date)}</td>
                        <td style={{...s.subTd,textAlign:'right'}} title={r.remark}>{r.remark}</td>
                        {[1,2,3,4].map(n=>(
                          <td key={n} style={s.subTd}>{r[`send_no${n}`]?`${toFa(r[`send_no${n}`])} — ${toFa(r[`send_date${n}`])}`:''}</td>))}
                        <td style={s.subTd}>{r.has_document?toFa(r.document_no):''}</td>
                      </tr>))}
                  </tbody>
                </table>
              </div>
            </div>
            <div style={s.mFooter}>
              <button onClick={()=>{ if(transMode==='view') setModal(null); }} disabled={transMode!=='view'}
                style={{...s.btnCancel,opacity:transMode==='view'?1:.4}}>بستن</button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div style={s.toast}>{toast}</div>}
    </div>
  );
}

const s = {
  page:{height:'100vh',overflow:'hidden',background:'#f0f2f7',fontFamily:"'Vazirmatn',Tahoma,sans-serif",direction:'rtl',display:'flex',flexDirection:'column'},
  header:{background:'linear-gradient(135deg,#0a1628,#1a2f50)',padding:'10px 24px',display:'flex',justifyContent:'space-between',alignItems:'center',boxShadow:'0 2px 16px rgba(0,0,0,0.3)',flexShrink:0},
  hLeft:{display:'flex',alignItems:'center',gap:'12px'},
  backBtn:{padding:'6px 14px',background:'rgba(255,255,255,0.12)',color:'#fff',border:'1px solid rgba(255,255,255,0.25)',borderRadius:'7px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  hLogo:{width:'40px',height:'40px',background:'rgba(200,168,75,0.12)',border:'1px solid rgba(200,168,75,0.3)',borderRadius:'9px',display:'flex',alignItems:'center',justifyContent:'center'},
  hTitle:{fontSize:'15px',fontWeight:'700',color:'#fff'},
  hSub:{fontSize:'11px',color:'rgba(255,255,255,0.55)'},
  userName:{fontSize:'13px',color:'rgba(255,255,255,0.85)'},
  main:{padding:'8px 20px',flex:1,display:'flex',flexDirection:'column',overflow:'auto',minHeight:0},
  toolbar:{display:'flex',alignItems:'center',gap:'8px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolbar2:{display:'flex',alignItems:'flex-end',gap:'12px',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',marginBottom:'6px',flexWrap:'wrap',flexShrink:0},
  toolBtns:{display:'flex',gap:'7px',flexWrap:'wrap'},
  radio:{display:'flex',alignItems:'center',gap:'5px',fontSize:'12px',fontWeight:'600',color:'#444',cursor:'pointer'},
  fL:{fontSize:'11px',fontWeight:'600',color:'#555',marginBottom:'3px'},
  fI:{padding:'8px 12px',border:'1.5px solid #e0e0e0',borderRadius:'8px',fontSize:'13px',fontFamily:"'Vazirmatn',Tahoma,sans-serif",outline:'none',direction:'rtl',textAlign:'right',boxSizing:'border-box',width:'100%'},
  btnNew:{padding:'7px 16px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  btnEdit:{padding:'7px 16px',background:'linear-gradient(135deg,#1a237e,#283593)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnDel:{padding:'7px 16px',background:'linear-gradient(135deg,#b71c1c,#c62828)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnCancelOp:{padding:'7px 16px',background:'linear-gradient(135deg,#6a1b9a,#8e24aa)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnGiveOp:{padding:'7px 16px',background:'linear-gradient(135deg,#bf360c,#e64a19)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnTrans:{padding:'7px 16px',background:'linear-gradient(135deg,#00695c,#00897b)',color:'#fff',border:'none',borderRadius:'8px',fontFamily:'inherit',fontSize:'13px',fontWeight:'700',cursor:'pointer'},
  btnMini:{padding:'3px 9px',background:'#e3f2fd',color:'#0d47a1',border:'1px solid #90caf9',borderRadius:'6px',cursor:'pointer',fontFamily:'inherit',fontSize:'11px',fontWeight:'700'},
  tableWrap:{overflowX:'auto',overflowY:'auto',borderRadius:'10px',boxShadow:'0 2px 8px rgba(0,0,0,0.1)',background:'#fff',flex:1,minHeight:'260px'},
  td:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'},
  num:{direction:'ltr',textAlign:'left',fontWeight:'700'},
  empty:{textAlign:'center',padding:'40px',color:'#999',fontSize:'13px'},
  summary:{display:'flex',gap:'18px',flexWrap:'wrap',background:'#fff',padding:'8px 14px',borderRadius:'10px',boxShadow:'0 1px 6px rgba(0,0,0,0.08)',margin:'6px 0',fontSize:'12px',color:'#444',flexShrink:0},
  subTh:{padding:'7px 9px',background:'#0d2137',color:'#C8A84B',fontSize:'11px',fontWeight:'700',whiteSpace:'nowrap',position:'sticky',top:0,zIndex:2},
  subTd:{padding:'6px 8px',borderBottom:'1px solid #f0f0f0',textAlign:'center',fontSize:'12px',whiteSpace:'nowrap'},
  overlay:{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'16px'},
  modal:{background:'#fff',borderRadius:'16px',width:'100%',maxWidth:'760px',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.4)'},
  mHeader:{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'10px',padding:'14px 20px',background:'linear-gradient(135deg,#0a1628,#1a2f50)',borderRadius:'16px 16px 0 0'},
  mTitle:{fontSize:'14px',fontWeight:'700',color:'#fff'},
  mTabs:{display:'flex',gap:'4px',padding:'8px 16px 0',background:'#f8f9fa',borderBottom:'1px solid #e0e0e0'},
  mTab:{padding:'7px 16px',background:'transparent',border:'none',borderBottom:'3px solid transparent',cursor:'pointer',fontFamily:'inherit',fontSize:'12px',fontWeight:'700',color:'#888'},
  mTabActive:{color:'#1a2f50',borderBottomColor:'#C8A84B',background:'#fff'},
  closeBtn:{background:'rgba(255,255,255,0.15)',border:'1px solid rgba(255,255,255,0.3)',color:'#fff',width:'30px',height:'30px',borderRadius:'50%',cursor:'pointer',fontSize:'13px',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0},
  mBody:{overflowY:'auto',flex:1,padding:'18px'},
  mFooter:{display:'flex',justifyContent:'flex-end',gap:'10px',padding:'12px 18px',borderTop:'1px solid #f0f2f7',background:'#f8f9fa',borderRadius:'0 0 16px 16px'},
  row:{display:'flex',gap:'12px',marginBottom:'12px',flexWrap:'wrap'},
  fset:{flex:'1 1 60%',border:'1px solid #b2dfdb',borderRadius:'8px',padding:'4px 10px 0',margin:0},
  legend:{fontSize:'12px',fontWeight:'700',color:'#00695c',padding:'0 6px'},
  transForm:{background:'#fafafa',border:'1px solid #eee',borderRadius:'10px',padding:'14px 14px 2px'},
  errBox:{background:'#fff3f3',border:'1px solid #ffcdd2',borderRadius:'8px',padding:'10px 14px',color:'#c62828',marginBottom:'10px',fontSize:'13px'},
  warnBox:{background:'#fff8e1',border:'1px solid #ffe082',borderRadius:'8px',padding:'9px 14px',color:'#e65100',marginBottom:'12px',fontSize:'12px'},
  infoBox:{background:'#e3f2fd',border:'1px solid #90caf9',borderRadius:'8px',padding:'9px 14px',color:'#0d47a1',fontSize:'12px',lineHeight:'1.9'},
  dropdown:{position:'absolute',top:'100%',left:0,right:0,background:'#fff',border:'1px solid #ddd',borderRadius:'8px',maxHeight:'220px',overflowY:'auto',zIndex:50,boxShadow:'0 6px 20px rgba(0,0,0,0.18)'},
  dropItem:{padding:'8px 11px',fontSize:'12px',cursor:'pointer',borderBottom:'1px solid #f5f5f5',textAlign:'right'},
  btnCancel:{padding:'8px 18px',background:'#e0e0e0',color:'#333',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px'},
  btnSave:{padding:'8px 22px',background:'linear-gradient(135deg,#1b5e20,#2e7d32)',color:'#fff',border:'none',borderRadius:'8px',cursor:'pointer',fontFamily:'inherit',fontSize:'13px',fontWeight:'700'},
  toast:{position:'fixed',bottom:'24px',right:'24px',background:'#1b5e20',color:'#fff',padding:'12px 24px',borderRadius:'10px',fontSize:'14px',fontWeight:'600',zIndex:2000,boxShadow:'0 4px 16px rgba(0,0,0,0.25)'},
};
